import React, { useCallback, useMemo, useRef, useState } from 'react'
import AlertDialog from '../../../../components/dialogs/AlertDialog'
import { intl } from '../../../../../i18n'

type ConfirmDialogProps = {
  open: boolean
  title: string
  message: string
  confirm?: () => void
  cancel?: () => void
}
const ConfirmDialog = ({
  open,
  title,
  message,
  confirm,
  cancel,
}: ConfirmDialogProps) => {
  return (
    <AlertDialog
      isOpen={open}
      title={title}
      message={message}
      submitButtonTitle={intl.formatMessage({ id: 'dialog.ok' })}
      submitHandler={confirm}
      closeButtonTitle={intl.formatMessage({ id: 'dialog.cancel' })}
      closeHandler={cancel}
    />
  )
}

export default ConfirmDialog

export type OpenConfirmDialog = (
  props: Omit<ConfirmDialogProps, 'open'>
) => void
export const useConfirmDialog = (): {
  openConfirmDialog: OpenConfirmDialog
  confirmDialogProps: ConfirmDialogProps
} => {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const confirm = useRef<(() => void) | undefined>(undefined)
  const cancel = useRef<(() => void) | undefined>(undefined)
  const openConfirmDialog = useCallback(
    ({
      title: _title,
      message: _message,
      confirm: _confirm,
      cancel: _cancel,
    }: Omit<ConfirmDialogProps, 'open'>) => {
      setOpen(true)
      setTitle(_title)
      setMessage(_message)
      confirm.current = _confirm
      cancel.current = _cancel
    },
    []
  )
  const onConfirm = useCallback(() => {
    confirm.current && confirm.current()
    setOpen(false)
  }, [])
  const onCancel = useCallback(() => {
    cancel.current && cancel.current()
    setOpen(false)
  }, [])

  const confirmDialogProps = useMemo<ConfirmDialogProps>(
    () => ({
      open,
      title,
      message,
      confirm: onConfirm,
      cancel: onCancel,
    }),
    [open, title, message, onConfirm, onCancel]
  )

  return { openConfirmDialog, confirmDialogProps }
}
